/**
 * Login - 登入頁面
 * 系統入口,提供 OAuth 登入選項
 */
import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';

export default function Login() {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // 如果已登入,重定向到 Dashboard
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  // 載入可用的 OAuth 提供商
  useEffect(() => {
    loadProviders();
  }, []);

  const loadProviders = async () => {
    try {
      const response = await api.get('/oauth/providers');
      setProviders(response.data.providers || []);
    } catch (err) {
      console.error('無法載入登入選項:', err);
      setError('無法載入登入選項,請稍後再試');
    }
  };

  const handleLogin = async (providerName) => {
    setLoading(true);
    setError(null);
    try {
      await login(providerName);
    } catch (err) {
      setError('登入失敗,請稍後再試');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8">
        {/* Logo 和標題 */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-indigo-600 rounded-full mb-4">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            Azure 帳單管理系統
          </h1>
          <p className="text-gray-600">
            請選擇登入方式以繼續
          </p>
        </div>

        {/* 錯誤訊息 */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}

        {/* OAuth 登入按鈕 */}
        <div className="space-y-3">
          {providers.length === 0 ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-4"></div>
              <p className="text-gray-500">載入登入選項...</p>
            </div>
          ) : (
            providers.map((provider) => (
              <button
                key={provider.provider_name}
                onClick={() => handleLogin(provider.provider_name)}
                disabled={loading}
                className={`
                  w-full flex items-center justify-center gap-3 px-6 py-4
                  bg-white border-2 border-gray-300 rounded-lg
                  hover:border-indigo-500 hover:bg-indigo-50
                  transition-all duration-200
                  ${loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                  group
                `}
              >
                {/* Microsoft Logo */}
                {provider.provider_name === 'microsoft' && (
                  <svg className="w-6 h-6" viewBox="0 0 23 23" fill="none">
                    <path d="M0 0h11v11H0z" fill="#f25022"/>
                    <path d="M12 0h11v11H12z" fill="#00a4ef"/>
                    <path d="M0 12h11v11H0z" fill="#7fba00"/>
                    <path d="M12 12h11v11H12z" fill="#ffb900"/>
                  </svg>
                )}
                
                <span className="text-gray-700 font-medium group-hover:text-indigo-700">
                  {provider.display_name}
                </span>

                {loading && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
                )}
              </button>
            ))
          )}
        </div>

        {/* 說明文字 */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-500">
            登入即表示您同意我們的服務條款和隱私政策
          </p>
        </div>
      </div>
    </div>
  );
}
